import { getServiceStatusNamesByStatusGroup } from './services';
import { ACTIVITY_STATUS_COLUMNS } from '../constants/service-activity.constant';

export interface ServicesFilterTarget {
  clientCode?: string;
  serviceTypeName?: string;
  /** Expanded by the job list into explicit status chips. */
  serviceStatusGroup?: string;
  /** Explicit status names, emitted as repeated `serviceStatusName` keys. */
  serviceStatusNames?: readonly string[];
}

/**
 * Every status name the activity monitor counts — the union of all ten status
 * groups, deduped.
 *
 * Derived from `getServiceStatusNamesByStatusGroup` rather than hand-listed so
 * it cannot drift from the groups the columns drill into.
 *
 * Needed because of how the job list decides its default filter: a URL
 * carrying `clientCode` sets `hasChipState`, which seeds `filtersInitialisedRef`
 * in `useServiceListData.ts` and makes the "all open statuses" baseline effect
 * return early. A client-only drill-through would therefore return *every*
 * status — Complete, Cancelled, Futile included — and the destination count
 * would not match the number the user just clicked. Sending these names
 * explicitly scopes the result to exactly the jobs the matrix counted.
 */
export const OPEN_STATUS_NAMES: readonly string[] = [
  ...new Set(
    ACTIVITY_STATUS_COLUMNS.flatMap((column) =>
      getServiceStatusNamesByStatusGroup(column.group)
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean)
    )
  ),
];

/**
 * Build a job-list URL from any subset of the activity-monitor filter keys.
 *
 * `parseServiceListUrl` reads `clientCode`, `serviceTypeName`,
 * `serviceStatusGroup` and `serviceStatusName` independently, so every subset
 * below yields a valid filtered list:
 *
 * | Drill level    | Keys emitted                                        |
 * |----------------|-----------------------------------------------------|
 * | Cell           | clientCode + serviceTypeName + serviceStatusGroup   |
 * | Column header  | serviceStatusGroup                                  |
 * | Row            | clientCode + serviceTypeName + open serviceStatusName |
 * | Client group   | clientCode + open serviceStatusName                 |
 *
 * Uses `URLSearchParams` rather than string concatenation: the previous
 * builder interpolated raw values, so a `&`, `#`, `?` or `%` in a client code
 * or service type silently truncated or corrupted the filter. Repeated keys
 * (not a comma-joined value) match how `parseStringListParam` reads them via
 * `searchParams.getAll`.
 */
export const buildServicesFilterURL = (target: ServicesFilterTarget): string => {
  const params = new URLSearchParams();

  if (target.clientCode) params.set('clientCode', target.clientCode);
  if (target.serviceTypeName) params.set('serviceTypeName', target.serviceTypeName);
  if (target.serviceStatusGroup) params.set('serviceStatusGroup', target.serviceStatusGroup);
  for (const name of target.serviceStatusNames ?? []) {
    if (name) params.append('serviceStatusName', name);
  }

  const search = params.toString();
  return search ? `/services?${search}` : '/services';
};
